import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDepartments } from '../../../hooks/useDepartments';
import { stateAllocationService } from '../../../services/stateAllocationService';
import { stateDepartmentsService } from '../../../services/stateDepartmentsService';
import { userService } from '../../../services/userService';
import LoadingSpinner from '../../common/LoadingSpinner';
import UniversalFilters from '../../common/UniversalFilters';
import DebouncedInput from '../../ui/DebouncedInput';
import { getFilterConfig } from '../../../constants/filterConfigs';
import { USER_ROLES } from '../../../constants/authConstants';
import { useFilters } from '../../../hooks/useFilters';
import { DEFAULT_PROJECT_ALLOCATION } from '../../../constants/stateAllocationConstants';
import { financialYearUtils, debounce, arrayUtils, dateUtils } from '../../../utils/helpers';
import { validateFundingPattern } from '../../../utils/stateAllocationValidation';
import ExportExcelButton from '../../common/ExportExcelButton';
import { exportProjectWiseAllocationToExcel } from '../../../utils/excelExport';
import { LOCK_MESSAGES } from '../../../constants/projectLockConstants';

const ProjectWiseAllocation = () => {
  const { user } = useAuth();
  const { isMobile, getResponsiveValue } = useTheme();
  const { departments, loading: loadingDepartments, getDepartmentLabel, getDepartmentOptions } = useDepartments();
  
  const isAdmin = user?.role === USER_ROLES.ADMIN;
  
  const [loading, setLoading] = useState(true);
  const [allocations, setAllocations] = useState([]);
  const [filteredAllocations, setFilteredAllocations] = useState([]);
  const [departmentsData, setDepartmentsData] = useState({});
  const [departmentApprovedAmounts, setDepartmentApprovedAmounts] = useState({});
  const [exportingExcel, setExportingExcel] = useState(false);
  
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(
    financialYearUtils.getActualCurrentFinancialYear()
  );
  
  const [viewAllYears, setViewAllYears] = useState(false);
  
  const {
    filters,
    handleFilterChange,
    clearFilters,
    hasActiveFilters
  } = useFilters({
    search: '',
    financialYear: financialYearUtils.getActualCurrentFinancialYear(),
    department: ''
  });

  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState({});
  const [userCache, setUserCache] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showUpdatedByColumn, setShowUpdatedByColumn] = useState(false);
  
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const filterConfig = getFilterConfig('PROJECT_ALLOCATION');

  const titleSize = getResponsiveValue({
    mobile: 'text-xl',
    desktop: 'text-2xl'
  });

  // CALCULATION HELPERS
  const calculateTotalAmount = (allocation) => {
    const cleared = parseFloat(allocation.clearedAgainstAllocation) || 0;
    const commonPool = parseFloat(allocation.clearedFromCommonPool) || 0;
    const additional = parseFloat(allocation.additionalStateShare) || 0;
    return cleared + commonPool + additional;
  };

  const formatCalculatedAmount = (amount) => {
    if (!amount && amount !== 0) return '₹0.00';
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return '₹0.00';
    return `₹${numAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatApprovedAmount = (amount) => {
    if (!amount && amount !== 0) return '₹0.00';
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return '₹0.00';
    return `₹${numAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatAggregatedAmount = (amount) => {
    if (!amount && amount !== 0) return '₹0.00';
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return '₹0.00';
    return `₹${numAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getDepartmentApprovedAmount = (department) => {
    return departmentApprovedAmounts[department] || 0;
  };

  const calculateDepartmentBalance = (department, departmentAggregates) => {
    const approvedAmount = getDepartmentApprovedAmount(department);
    const clearedAmount = departmentAggregates.clearedAgainstAllocation;
    const balance = approvedAmount - clearedAmount;
    return Math.max(0, balance);
  };

  const calculateDepartmentAggregates = (departmentAllocations) => {
    return departmentAllocations.reduce((totals, allocation) => {
      const clearedAgainstAllocation = parseFloat(allocation.clearedAgainstAllocation) || 0;
      const clearedFromCommonPool = parseFloat(allocation.clearedFromCommonPool) || 0;
      const additionalStateShare = parseFloat(allocation.additionalStateShare) || 0;
      const calculatedTotalAmount = clearedAgainstAllocation + clearedFromCommonPool + additionalStateShare;

      return {
        clearedAgainstAllocation: totals.clearedAgainstAllocation + clearedAgainstAllocation,
        clearedFromCommonPool: totals.clearedFromCommonPool + clearedFromCommonPool,
        additionalStateShare: totals.additionalStateShare + additionalStateShare,
        totalAmount: totals.totalAmount + calculatedTotalAmount
      };
    }, {
      clearedAgainstAllocation: 0,
      clearedFromCommonPool: 0,
      additionalStateShare: 0,
      totalAmount: 0
    });
  };

  // USER MANAGEMENT
  const getUserDisplayName = async (userId) => {
    if (!userId) return 'System';
    if (userCache[userId]) return userCache[userId];
    
    try {
      const userData = await userService.getUserById(userId);
      const displayName = userData ? userData.fullName || userData.email || 'Unknown User' : 'Unknown User';
      setUserCache(prev => ({ ...prev, [userId]: displayName }));
      return displayName;
    } catch (error) {
      console.error('Error fetching user data:', error);
      return 'Unknown User';
    }
  };

  const formatLastUpdated = (updatedBy, updatedAt) => {
    const userId = updatedBy || 'system';
    const userDisplayName = userCache[userId] || (loadingUsers ? 'Loading...' : 'Unknown User');
    const timeAgo = updatedAt ? dateUtils.formatDateTime(updatedAt) : 'Unknown';
    return { user: userDisplayName, time: timeAgo };
  };

  const loadUserData = async (allocations) => {
    const userIds = [...new Set(allocations.map(a => a.updatedBy || a.createdBy).filter(Boolean))];
    if (userIds.length === 0) return;
    
    setLoadingUsers(true);
    try {
      await Promise.all(userIds.map(userId => getUserDisplayName(userId)));
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // PROJECT-LEVEL LOCK/UNLOCK FUNCTIONS
  const handleLockProject = async (allocationId) => {
    if (!allocationId) return;

    const allocation = allocations.find(a => a.id === allocationId);
    if (!allocation) return;

    if (allocation.isLocked) {
      alert(LOCK_MESSAGES.PROJECT_ALREADY_LOCKED);
      return;
    }

    if (!confirm(LOCK_MESSAGES.PROJECT_LOCK_CONFIRM)) return;

    try {
      const result = await stateAllocationService.lockProjectAllocation(
        allocationId,
        user.uid,
        user.displayName || user.email
      );

      if (result.success) {
        setAllocations(prev => prev.map(a => 
          a.id === allocationId 
            ? { ...a, isLocked: true, lockedAt: new Date(), lockedBy: user.uid, lockedByName: user.displayName || user.email }
            : a
        ));

        setSuccess(true);
        setSuccessMessage(LOCK_MESSAGES.PROJECT_LOCK_SUCCESS);
        setTimeout(() => {
          setSuccess(false);
          setSuccessMessage('');
        }, 3000);
      }
    } catch (error) {
      console.error('Error locking project:', error);
      alert('Failed to lock project: ' + error.message);
    }
  };

  const handleUnlockProject = async (allocationId) => {
    if (!isAdmin) {
      alert(LOCK_MESSAGES.PROJECT_LOCK_NO_PERMISSION);
      return;
    }

    if (!allocationId) return;

    const allocation = allocations.find(a => a.id === allocationId);
    if (!allocation) return;

    if (!allocation.isLocked) {
      alert(LOCK_MESSAGES.PROJECT_NOT_LOCKED);
      return;
    }

    if (!confirm(LOCK_MESSAGES.PROJECT_UNLOCK_CONFIRM)) return;

    try {
      const result = await stateAllocationService.unlockProjectAllocation(
        allocationId,
        user.uid,
        user.displayName || user.email
      );

      if (result.success) {
        setAllocations(prev => prev.map(a => 
          a.id === allocationId 
            ? { ...a, isLocked: false, lockedAt: null, lockedBy: null, lockedByName: null }
            : a
        ));

        setSuccess(true);
        setSuccessMessage(LOCK_MESSAGES.PROJECT_UNLOCK_SUCCESS);
        setTimeout(() => {
          setSuccess(false);
          setSuccessMessage('');
        }, 3000);
      }
    } catch (error) {
      console.error('Error unlocking project:', error);
      alert('Failed to unlock project: ' + error.message);
    }
  };

  const canEditProject = (allocation) => {
    if (isAdmin) return true;
    return !allocation.isLocked;
  };

  // FILTERING
  const applyFiltersAndSort = () => {
    if (!departments.length) return;
    
    let filtered = [...allocations];

    if (filters.search && filters.search.trim()) {
      const searchFields = ['projectName', 'department'];
      filtered = arrayUtils.filterBySearch(filtered, filters.search, searchFields);
    }

    if (filters.department) {
      filtered = filtered.filter(allocation => allocation.department === filters.department);
    }

    setFilteredAllocations(filtered);
  };

  // DATA LOADING
  const loadAllocations = async () => {
    try {
      setLoading(true);
      setError('');
      
      const filterParams = {};
      const fyToLoad = viewAllYears ? null : selectedFinancialYear;
      if (fyToLoad) filterParams.financialYear = fyToLoad;
      
      const result = await stateAllocationService.getProjectAllocations(filterParams);
      
      if (result.success) {
        const allocationsWithLock = result.data.map(allocation => ({
          ...allocation,
          isLocked: allocation.isLocked || false,
          lockedAt: allocation.lockedAt || null,
          lockedBy: allocation.lockedBy || null,
          lockedByName: allocation.lockedByName || null
        }));
        
        setAllocations(allocationsWithLock);
        await loadUserData(allocationsWithLock);
      }
    } catch (error) {
      console.error('Error loading allocations:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDepartmentsData = async () => {
    try {
      const fyToLoad = viewAllYears ? financialYearUtils.getActualCurrentFinancialYear() : selectedFinancialYear;
      const result = await stateDepartmentsService.getDepartmentsData(fyToLoad);
      
      if (result.success && result.data) {
        const deptData = {};
        result.data.departments?.forEach(dept => {
          deptData[dept.value || dept.id] = dept.amount || 0;
        });
        setDepartmentsData(deptData);
      }
    } catch (error) {
      console.error('Error loading departments data:', error);
    }
  };

  const loadDepartmentApprovedAmounts = async () => {
    try {
      const fyToLoad = viewAllYears ? financialYearUtils.getActualCurrentFinancialYear() : selectedFinancialYear;
      const result = await stateDepartmentsService.getDepartmentsData(fyToLoad);
      
      if (result.success && result.data && result.data.departments) {
        const approvedAmounts = {};
        result.data.departments.forEach(dept => {
          const deptKey = dept.value || dept.id;
          approvedAmounts[deptKey] = dept.amount || 0;
        });
        setDepartmentApprovedAmounts(approvedAmounts);
      }
    } catch (error) {
      console.error('Error loading department approved amounts:', error);
    }
  };

  // EFFECTS
  useEffect(() => {
    if (!viewAllYears) {
      handleFilterChange('financialYear', selectedFinancialYear);
    }
  }, [selectedFinancialYear, viewAllYears]);

  useEffect(() => {
    if (departments.length > 0) {
      loadAllocations();
      loadDepartmentsData();
      loadDepartmentApprovedAmounts();
    }
  }, [selectedFinancialYear, viewAllYears, departments]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [allocations, filters.search, filters.department, departments]);

  // HANDLERS
  const handleFinancialYearChange = (year) => {
    setSelectedFinancialYear(year);
    setViewAllYears(false);
  };

  const handleViewAllYears = () => {
    setViewAllYears(!viewAllYears);
  };

  const handleAddNew = async (departmentCode = null) => {
    try {
      const newAllocation = {
        ...DEFAULT_PROJECT_ALLOCATION,
        financialYear: selectedFinancialYear,
        department: departmentCode || filters.department || '',
        projectName: `New Project ${allocations.length + 1}`,
        approvedAllocation: 0,
        clearedAgainstAllocation: 0,
        clearedFromCommonPool: 0,
        additionalStateShare: 0,
        fundingPattern: '',
        correspondingCentralShare: 0,
        balanceStateShareAgainstAllocation: 0,
        isLocked: false
      };
      
      const result = await stateAllocationService.addProjectAllocation(newAllocation, user.uid);
      if (result.success) {
        setAllocations(prev => [result.data, ...prev]);
        setSuccess(true);
        setSuccessMessage(`New project allocation added for FY ${selectedFinancialYear}!`);
        setTimeout(() => {
          setSuccess(false);
          setSuccessMessage('');
        }, 3000);
      }
    } catch (error) {
      console.error('Error in handleAddNew:', error);
      setError('Failed to add new allocation: ' + error.message);
    }
  };

  const handleDelete = async (allocationId) => {
    const allocation = allocations.find(a => a.id === allocationId);
    
    if (!allocation) return;

    if (allocation.isLocked && !isAdmin) {
      alert(LOCK_MESSAGES.PROJECT_LOCKED_EDIT_WARNING);
      return;
    }

    if (!confirm('Are you sure you want to delete this allocation?')) return;
    
    try {
      await stateAllocationService.deleteProjectAllocation(allocationId);
      setAllocations(prev => prev.filter(a => a.id !== allocationId));
      setSuccess(true);
      setSuccessMessage('Allocation deleted successfully!');
      setTimeout(() => {
        setSuccess(false);
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      setError('Failed to delete allocation: ' + error.message);
    }
  };

  const handleFieldChange = (allocationId, field, value) => {
    const allocation = allocations.find(a => a.id === allocationId);
    
    if (!allocation) return;

    if (allocation.isLocked && !isAdmin) {
      alert(LOCK_MESSAGES.PROJECT_LOCKED_EDIT_WARNING);
      return;
    }

    setAllocations(prev => prev.map(a => {
      if (a.id !== allocationId) return a;
      return { ...a, [field]: value };
    }));
    
    setErrors(prev => ({
      ...prev,
      [`${allocationId}.${field}`]: ''
    }));
    
    if (field === 'fundingPattern') {
      const validationError = validateFundingPattern(value);
      if (validationError) {
        setErrors(prev => ({
          ...prev,
          [`${allocationId}.${field}`]: validationError
        }));
        return;
      }
    }
    
    const currentAllocation = allocations.find(a => a.id === allocationId);
    if (currentAllocation) {
      const updatedData = { ...currentAllocation, [field]: value };
      debouncedAutoSave(allocationId, updatedData);
    }
  };

  const debouncedAutoSave = useCallback(
    debounce(async (allocationId, allocationData) => {
      const allocation = allocations.find(a => a.id === allocationId);
      
      if (!allocation) return;

      if (allocation.isLocked && !isAdmin) {
        console.log('Cannot save: project is locked');
        return;
      }

      try {
        setSaving(prev => ({ ...prev, [allocationId]: true }));
        await stateAllocationService.updateProjectAllocation(allocationId, allocationData, user.uid);
        
        setAllocations(prev => prev.map(a => 
          a.id === allocationId 
            ? { ...a, ...allocationData, updatedBy: user.uid, updatedAt: new Date() }
            : a
        ));
        
        setSaving(prev => ({ ...prev, [allocationId]: false }));
        
        if (!userCache[user.uid]) {
          await getUserDisplayName(user.uid);
        }
      } catch (error) {
        console.error('Auto-save error:', error);
        setSaving(prev => ({ ...prev, [allocationId]: false }));
      }
    }, 1500),
    [user.uid, userCache, allocations, isAdmin]
  );

  const handleExportExcel = () => {
    try {
      setExportingExcel(true);
      const exportData = {
        allocations: filteredAllocations,
        departmentTotals: {},
        departmentApprovedAmounts: departmentApprovedAmounts
      };
      exportProjectWiseAllocationToExcel(exportData, filters, getDepartmentLabel);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      setError('Failed to export Excel. Please try again.');
    } finally {
      setExportingExcel(false);
    }
  };

  // GROUPED ALLOCATIONS
  const groupedAllocations = useMemo(() => {
    if (!filteredAllocations.length || !departments.length) return {};
    
    const grouped = arrayUtils.groupBy(filteredAllocations, 'department');
    const sortedGroups = {};
    
    const departmentKeys = Object.keys(grouped);
    const unknownDepts = departmentKeys.filter(dept => 
      !dept || dept === '' || dept === 'undefined' || dept === 'null' || 
      dept.toLowerCase() === 'unknown' || dept === 'Select Dept.' || dept === 'select dept.'
    );
    
    const knownDepts = departmentKeys.filter(dept => 
      dept && dept !== '' && dept !== 'undefined' && dept !== 'null' && 
      dept.toLowerCase() !== 'unknown' && dept !== 'Select Dept.' && dept !== 'select dept.'
    );
    
    unknownDepts.forEach(dept => {
      const displayKey = dept || 'unknown';
      sortedGroups[displayKey] = grouped[dept].sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
    });
    
    const sortedKnownDepts = knownDepts.sort((a, b) => {
      const deptA = departments.find(d => d.departmentCode === a);
      const deptB = departments.find(d => d.departmentCode === b);
      return (deptA?.priority || 999) - (deptB?.priority || 999);
    });
    
    sortedKnownDepts.forEach(dept => {
      sortedGroups[dept] = grouped[dept].sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
    });
    
    return sortedGroups;
  }, [filteredAllocations, departments]);

  const fyOptions = financialYearUtils.getExtendedFinancialYearOptions();

  if (loading || loadingDepartments) {
    return (
      <div className="min-h-screen bg-bg-secondary flex items-center justify-center">
        <LoadingSpinner size="xl" text="Loading project allocations..." />
      </div>
    );
  }

  return (
    <div>
      {success && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3 shadow-sm mb-6">
          <div className="flex items-center">
            <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="ml-2 text-green-700 font-medium text-sm">{successMessage}</span>
          </div>
        </div>
      )}
      
      <style>{`
        .table-container {
          max-height: calc(100vh - 320px);
          overflow-y: auto;
          position: relative;
        }
        
        .sticky-header thead {
          position: sticky;
          top: 0;
          z-index: 20;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .sticky-header thead th {
          background: linear-gradient(to right, #f9fafb, #f3f4f6);
        }
        
        .no-arrows::-webkit-outer-spin-button,
        .no-arrows::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .no-arrows[type=number] { -moz-appearance: textfield; }
        .enhanced-input:focus { transform: translateY(-1px); box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); }
        .enhanced-input { transition: all 0.2s ease-in-out; }
        .department-select {
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
          background-position: right 0.5rem center;
          background-repeat: no-repeat;
          background-size: 1.5em 1.5em;
          padding-right: 2.5rem;
        }
        .table-row:hover .enhanced-input { border-color: #3b82f6; }
        .dept-group-header { background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%); border-left: 4px solid #3b82f6; }
        .unknown-dept-header { background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); border-left: 4px solid #f59e0b; }
      `}</style>

      <div className="flex items-center justify-between mb-6">
        <div>
          {selectedFinancialYear && !viewAllYears && (
            <p className="text-3xl font-semibold text-blue-600 mt-1">
              {financialYearUtils.getFinancialYearDisplay(selectedFinancialYear)}
            </p>
          )}
          <h1 className={`${titleSize} font-semibold text-text-primary mb-1`}>Project Wise</h1>
          <p className="text-text-secondary text-sm">Manage project-level fund allocations and clearances</p>
        </div>

        <div className="flex items-center space-x-4">
          <ExportExcelButton onClick={handleExportExcel} loading={exportingExcel} disabled={filteredAllocations.length === 0} />
          
          <button
            onClick={() => setShowUpdatedByColumn(!showUpdatedByColumn)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-all duration-200 ${
              showUpdatedByColumn
                ? 'bg-blue-500 border-blue-500 text-white shadow-md'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
            title={showUpdatedByColumn ? "Hide Updated By column" : "Show Updated By column"}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className="text-sm font-medium">
              {showUpdatedByColumn ? 'Hide' : 'Show'} Updated By
            </span>
          </button>

          <UniversalFilters
            config={filterConfig}
            values={filters}
            onChange={handleFilterChange}
            onClearFilters={clearFilters}
            mode="dropdown"
            dropdownProps={{
              buttonProps: { size: 'md' },
              contentProps: { layout: 'grid' }
            }}
          />
        </div>
      </div>

      <div className="bg-white border-2 border-purple-200 rounded-xl p-4 my-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-500 rounded-lg p-2.5">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-900">Financial Year</label>
              <p className="text-xs text-gray-600">View and add allocations for selected year</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <select
              value={selectedFinancialYear}
              onChange={(e) => handleFinancialYearChange(e.target.value)}
              disabled={viewAllYears}
              className={`px-4 py-2 text-sm font-medium border-2 rounded-lg transition-all duration-200 ${
                viewAllYears 
                  ? 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-white border-purple-300 text-gray-900 hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer'
              }`}
            >
              {fyOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}{option.isCurrent}
                </option>
              ))}
            </select>
            
            <button
              onClick={handleViewAllYears}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                viewAllYears
                  ? 'bg-purple-500 text-white border-2 border-purple-500 shadow-md'
                  : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-purple-300 hover:text-purple-600'
              }`}
            >
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                <span>{viewAllYears ? 'Viewing All Years' : 'View All Years'}</span>
              </div>
            </button>
          </div>
        </div>
        
        {!viewAllYears && (
          <div className="mt-3 pt-3 border-t border-purple-100">
            <p className="text-xs text-gray-600">
              <span className="font-medium text-purple-700">Note:</span> New allocations will be added to <span className="font-semibold text-purple-900">FY {selectedFinancialYear}</span>
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-error-subtle border border-border-error rounded-xl p-4 mb-6">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-error font-medium">{error}</span>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="table-container">
          <table className="min-w-full text-xs sticky-header">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>
                <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-10">S.No.</th>
                <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-64">Department</th>
                <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-40">Project Name</th>
                <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 bg-orange-50 w-20">
                  Cleared against allocation<br /><span className="text-gray-500 normal-case font-normal">(in lakh)</span>
                </th>
                <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 bg-orange-50 w-20">
                  Cleared from Common Pool<br /><span className="text-gray-500 normal-case font-normal">(in lakh)</span>
                </th>
                <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 bg-orange-50 w-20">
                  Additional State Share<br /><span className="text-gray-500 normal-case font-normal">(in lakh)</span>
                </th>
                <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 bg-orange-50 w-20">
                  Total Amount<br /><span className="text-gray-500 normal-case font-normal">(in lakh)</span>
                </th>
                <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 bg-purple-50 w-20">Funding Pattern</th>
                <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 bg-purple-50 w-20">
                  Central Share<br /><span className="text-gray-500 normal-case font-normal">(in lakh)</span>
                </th>
                <th className="px-2 py-2 text-center text-xs font-bold text-blue-700 uppercase tracking-wider border-r border-blue-200 bg-blue-50 w-20">
                  Approved Amount<br /><span className="text-gray-500 normal-case font-normal">(in lakh)</span>
                </th>
                <th className={`px-2 py-2 text-center text-xs font-bold text-green-700 uppercase tracking-wider bg-green-50 w-20 ${showUpdatedByColumn ? 'border-r border-green-200' : ''}`}>
                  Balance State Share against allocation<br /><span className="text-gray-500 normal-case font-normal">(in lakh)</span>
                </th>
                {showUpdatedByColumn && (
                  <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-24">Updated By</th>
                )}
                <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-20">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 border-t-2 border-blue-200">
                <td className="px-2 py-3 text-center border-r border-gray-200">
                  <button
                    onClick={() => handleAddNew()}
                    className="group flex items-center justify-center w-8 h-8 bg-blue-500 hover:bg-blue-600 text-white rounded-full transition-all duration-200 transform hover:scale-110 shadow-lg hover:shadow-xl"
                    title="Add New Project"
                  >
                    <svg className="w-4 h-4 group-hover:rotate-90 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </td>
                <td className="px-2 py-3 text-xs text-blue-600 font-medium border-r border-gray-200">Click + to add new project</td>
                <td className="px-2 py-3 border-r border-gray-200"></td>
                <td className="px-2 py-3 border-r border-gray-200 bg-orange-50"></td>
                <td className="px-2 py-3 border-r border-gray-200 bg-orange-50"></td>
                <td className="px-2 py-3 border-r border-gray-200 bg-orange-50"></td>
                <td className="px-2 py-3 border-r border-gray-200 bg-orange-50"></td>
                <td className="px-2 py-3 border-r border-gray-200 bg-purple-50"></td>
                <td className="px-2 py-3 border-r border-gray-200 bg-purple-50"></td>
                <td className="px-2 py-3 border-r border-gray-200 bg-blue-50"></td>
                <td className={`px-2 py-3 bg-green-50 ${showUpdatedByColumn ? 'border-r border-gray-200' : ''}`}></td>
                {showUpdatedByColumn && <td className="px-2 py-3 border-r border-gray-200"></td>}
                <td className="px-2 py-3"></td>
              </tr>
              {Object.keys(groupedAllocations).length > 0 ? (
                (() => {
                  let globalIndex = 0;
                  return Object.entries(groupedAllocations).map(([department, departmentAllocations]) => {
                    const isUnknownDept = !department || department === '' || department === 'unknown' || department === 'Select Dept.';
                    const departmentApprovedAmount = getDepartmentApprovedAmount(department);
                    const departmentAggregates = calculateDepartmentAggregates(departmentAllocations);
                    const departmentBalance = calculateDepartmentBalance(department, departmentAggregates);
                    
                    return (
                      <React.Fragment key={department}>
                        {departmentAllocations.length > 0 && (
                          <tr className={isUnknownDept ? "unknown-dept-header" : "dept-group-header"}>
                            <td className="px-3 py-2 border-r border-gray-200">
                              <button
                                onClick={() => handleAddNew(department)}
                                className="group flex items-center justify-center w-6 h-6 bg-green-500 hover:bg-green-600 text-white rounded-full transition-all duration-200 transform hover:scale-110 shadow-md hover:shadow-lg"
                                title={`Add project to ${getDepartmentLabel(department)}`}
                              >
                                <svg className="w-3.5 h-3.5 group-hover:rotate-90 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                              </button>
                            </td>
                            <td className="px-3 py-2 text-sm font-bold text-gray-800 border-r border-gray-200">
                              <div className="flex items-center space-x-2">
                                {isUnknownDept ? (
                                  <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                  </svg>
                                ) : (
                                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                  </svg>
                                )}
                                <span>
                                  {isUnknownDept 
                                    ? `New Projects (${departmentAllocations.length} project${departmentAllocations.length !== 1 ? 's' : ''})`
                                    : `${getDepartmentLabel(department)} (${departmentAllocations.length} project${departmentAllocations.length !== 1 ? 's' : ''})`
                                  }
                                </span>
                                {isUnknownDept && <span className="text-xs text-amber-600 font-normal">- Please select departments</span>}
                              </div>
                            </td>
                            <td className="px-3 py-2 border-r border-gray-200"></td>
                            <td className="px-3 py-2 text-center border-r border-gray-200 bg-orange-100">
                              <div className="text-sm font-bold text-gray-900">{formatAggregatedAmount(departmentAggregates.clearedAgainstAllocation)}</div>
                            </td>
                            <td className="px-3 py-2 text-center border-r border-gray-200 bg-orange-100">
                              <div className="text-sm font-bold text-gray-900">{formatAggregatedAmount(departmentAggregates.clearedFromCommonPool)}</div>
                            </td>
                            <td className="px-3 py-2 text-center border-r border-gray-200 bg-orange-100">
                              <div className="text-sm font-bold text-gray-900">{formatAggregatedAmount(departmentAggregates.additionalStateShare)}</div>
                            </td>
                            <td className="px-3 py-2 text-center border-r border-gray-200 bg-orange-100">
                              <div className="text-sm font-bold text-gray-900">{formatAggregatedAmount(departmentAggregates.totalAmount)}</div>
                            </td>
                            <td className="px-3 py-2 border-r border-gray-200 bg-purple-100"></td>
                            <td className="px-3 py-2 border-r border-gray-200 bg-purple-100"></td>
                            <td className="px-3 py-2 text-center border-r border-blue-200 bg-blue-100">
                              {!isUnknownDept && department ? (
                                <div className="text-sm font-bold text-blue-900">{formatApprovedAmount(departmentApprovedAmount)}</div>
                              ) : (
                                <div className="text-sm text-gray-400">—</div>
                              )}
                            </td>
                            <td className={`px-3 py-2 text-center bg-green-100 ${showUpdatedByColumn ? 'border-r border-green-200' : ''}`}>
                              {!isUnknownDept && department ? (
                                <div className="text-sm font-bold text-green-900">{formatAggregatedAmount(departmentBalance)}</div>
                              ) : (
                                <div className="text-sm text-gray-400">—</div>
                              )}
                            </td>
                            {showUpdatedByColumn && (
                              <td className="px-3 py-2 text-center border-r border-gray-200">
                                <div className="text-xs text-gray-400">—</div>
                              </td>
                            )}
                            <td className="px-3 py-2"></td>
                          </tr>
                        )}
                        
                        {departmentAllocations.map((allocation) => {
                          globalIndex++;
                          const isAllocationSaving = saving[allocation.id];
                          const lastUpdated = formatLastUpdated(allocation.updatedBy || allocation.createdBy, allocation.updatedAt || allocation.createdAt);
                          const calculatedTotalAmount = calculateTotalAmount(allocation);
                          const isProjectLocked = allocation.isLocked || false;
                          const canEdit = canEditProject(allocation);
                          
                          return (
                            <tr key={allocation.id} className={`table-row hover:bg-blue-50 transition-colors duration-200 ${isProjectLocked ? 'bg-orange-50' : ''}`}>
                              <td className="px-2 py-2 text-xs text-gray-600 font-semibold border-r border-gray-200">
                                <div className="flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full text-xs font-bold">
                                  {globalIndex}
                                </div>
                              </td>
                              <td className="px-2 py-2 border-r border-gray-200">
                                <select
                                  value={allocation.department || ''}
                                  onChange={(e) => handleFieldChange(allocation.id, 'department', e.target.value)}
                                  disabled={!canEdit}
                                  className={`enhanced-input department-select w-full text-xs border border-gray-300 rounded-lg px-1 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                  <option value="">Select Dept.</option>
                                  {getDepartmentOptions().map(dept => (
                                    <option key={dept.value} value={dept.value}>
                                      {dept.label}
                                    </option>
                                  ))}
                                </select>
                                {isAllocationSaving && (
                                  <div className="flex items-center mt-1">
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
                                    <span className="ml-1 text-xs text-blue-600">Saving...</span>
                                  </div>
                                )}
                              </td>
                              <td className="px-2 py-2 border-r border-gray-200">
                                <DebouncedInput
                                  type="text"
                                  value={allocation.projectName || ''}
                                  onChange={(value) => handleFieldChange(allocation.id, 'projectName', value)}
                                  disabled={!canEdit}
                                  delay={1200}
                                  className={`enhanced-input w-full text-xs border border-gray-300 rounded-lg px-1 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  placeholder="Enter project name"
                                />
                              </td>
                              <td className="px-2 py-2 text-center border-r border-gray-200 bg-orange-50">
                                <DebouncedInput
                                  type="number"
                                  value={allocation.clearedAgainstAllocation || ''}
                                  onChange={(value) => handleFieldChange(allocation.id, 'clearedAgainstAllocation', value)}
                                  disabled={!canEdit}
                                  delay={1200}
                                  className={`enhanced-input no-arrows w-full text-xs text-center border border-gray-300 rounded-lg px-1 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  placeholder="0"
                                />
                              </td>
                              <td className="px-2 py-2 text-center border-r border-gray-200 bg-orange-50">
                                <DebouncedInput
                                  type="number"
                                  value={allocation.clearedFromCommonPool || ''}
                                  onChange={(value) => handleFieldChange(allocation.id, 'clearedFromCommonPool', value)}
                                  disabled={!canEdit}
                                  delay={1200}
                                  className={`enhanced-input no-arrows w-full text-xs text-center border border-gray-300 rounded-lg px-1 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  placeholder="0"
                                />
                              </td>
                              <td className="px-2 py-2 text-center border-r border-gray-200 bg-orange-50">
                                <DebouncedInput
                                  type="number"
                                  value={allocation.additionalStateShare || ''}
                                  onChange={(value) => handleFieldChange(allocation.id, 'additionalStateShare', value)}
                                  disabled={!canEdit}
                                  delay={1200}
                                  className={`enhanced-input no-arrows w-full text-xs text-center border border-gray-300 rounded-lg px-1 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  placeholder="0"
                                />
                              </td>
                              <td className="px-2 py-2 text-center border-r border-gray-200 bg-orange-50">
                                <div className="text-xs font-bold text-gray-900">
                                  {formatCalculatedAmount(calculatedTotalAmount)}
                                </div>
                              </td>
                              <td className="px-2 py-2 text-center border-r border-gray-200 bg-purple-50">
                                <DebouncedInput
                                  type="text"
                                  value={allocation.fundingPattern || ''}
                                  onChange={(value) => handleFieldChange(allocation.id, 'fundingPattern', value)}
                                  disabled={!canEdit}
                                  delay={1200}
                                  className={`enhanced-input w-full text-xs text-center border border-gray-300 rounded-lg px-1 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                    errors[`${allocation.id}.fundingPattern`] ? 'border-red-500 focus:ring-red-500' : ''
                                  } ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  placeholder="0"
                                  title="Enter funding pattern like 60:40 or 10:20:70"
                                  maxLength="20"
                                />
                                {errors[`${allocation.id}.fundingPattern`] && (
                                  <div className="text-xs text-red-600 mt-0.5 font-medium">
                                    {errors[`${allocation.id}.fundingPattern`]}
                                  </div>
                                )}
                              </td>
                              <td className="px-2 py-2 text-center border-r border-gray-200 bg-purple-50">
                                <DebouncedInput
                                  type="number"
                                  value={allocation.correspondingCentralShare || ''}
                                  onChange={(value) => handleFieldChange(allocation.id, 'correspondingCentralShare', value)}
                                  disabled={!canEdit}
                                  delay={1200}
                                  className={`enhanced-input no-arrows w-full text-xs text-center border border-gray-300 rounded-lg px-1 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  placeholder="0"
                                />
                              </td>
                              <td className="px-2 py-2 text-center border-r border-gray-200 bg-blue-50">
                                <div className="text-xs text-gray-400">—</div>
                              </td>
                              <td className={`px-2 py-2 text-center bg-green-50 ${showUpdatedByColumn ? 'border-r border-gray-200' : ''}`}>
                                <div className="text-xs text-gray-400">—</div>
                              </td>
                              {showUpdatedByColumn && (
                                <td className="px-2 py-2 text-center border-r border-gray-200">
                                  <div className="text-xs text-gray-600">
                                    <div className="font-medium text-gray-800 mb-0.5">
                                      {lastUpdated.user}
                                    </div>
                                    <div className="text-gray-500">
                                      {lastUpdated.time}
                                    </div>
                                  </div>
                                </td>
                              )}
                              <td className="px-2 py-2 text-center">
                                <div className="flex items-center justify-center space-x-1">
                                  {isProjectLocked ? (
                                    isAdmin ? (
                                      <button
                                        onClick={() => handleUnlockProject(allocation.id)}
                                        className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                        title={`Unlock project (Locked by ${allocation.lockedByName})`}
                                      >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                        </svg>
                                      </button>
                                    ) : (
                                      <div className="p-1.5 text-orange-400" title={`Locked by ${allocation.lockedByName}`}>
                                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                        </svg>
                                      </div>
                                    )
                                  ) : (
                                    <button
                                      onClick={() => handleLockProject(allocation.id)}
                                      disabled={saving[allocation.id]}
                                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                                      title="Lock project"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                      </svg>
                                    </button>
                                  )}
                                  {canEdit ? (
                                    <button
                                      onClick={() => handleDelete(allocation.id)}
                                      disabled={saving[allocation.id]}
                                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                      title="Delete allocation"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  });
                })()
              ) : (
                <tr>
                  <td colSpan={showUpdatedByColumn ? "13" : "12"} className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center space-y-3">
                      <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-900">No project allocations found</p>
                        <p className="text-sm text-gray-500">
                          {hasActiveFilters 
                            ? "Try adjusting your filters to see more allocations."
                            : "Add a new project allocation to get started."
                          }
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProjectWiseAllocation;